import type { ChartColumnConfig, XAxisConfig } from '@repo/api-codegen';
import dayjs from 'dayjs';

function formatCompactNumber(
	value: number,
	options: Intl.NumberFormatOptions = {}
): string {
	const formatter = new Intl.NumberFormat('en-US', {
		...options,
		notation: 'compact',
	});

	const formatted = formatter.format(value);

	return formatted.replace(/[KMBT]/g, (match) => match.toLowerCase());
}

export function getPointValueFromChannel<T, P>(
	channel: keyof T | ((d: T) => P),
	item: T
): P {
	// Type assertion is necessary because TypeScript can't guarantee that T[keyof T] extends P.
	// Chart components are responsible for ensuring their data types match their channel specs.
	return typeof channel === 'function' ? channel(item) : (item[channel] as P);
}

// Helper function to format values based on column format configuration
export function formatValue(
	value: number | string | Date | null,
	columnFormat: ChartColumnConfig,
	xAxisTimeInterval: XAxisConfig['xAxisTimeInterval'] = 'auto'
): string {
	if (!columnFormat) return String(value);

	const {
		displayNumberAs,
		currency = 'USD',
		dateFormat = 'MMM D',
		columnType,
		convertNumberTo,
		compactNumbers = true,
		// dateDisplayAsRelative,
		// dateParseAsUtc,
		// replaceMissingDataWith,
	} = columnFormat;
	const multiplier = 1;
	const minimumFractionDigits = 0;
	const maximumFractionDigits = 2;

	// Ensure prefix and suffix are strings, not null
	const safePrefix = '';
	const safeSuffix = '';

	// Handle number to month conversion
	if (convertNumberTo === 'month_of_year' && typeof value === 'number') {
		const monthNames = [
			'Jan',
			'Feb',
			'Mar',
			'Apr',
			'May',
			'Jun',
			'Jul',
			'Aug',
			'Sep',
			'Oct',
			'Nov',
			'Dec',
		];
		// Convert 1-based month number to month name
		const monthIndex = value - 1;
		if (monthIndex >= 0 && monthIndex < 12) {
			return monthNames[monthIndex];
		}
		return String(value); // Fallback if invalid month number
	}

	// Handle number to day of week conversion
	if (convertNumberTo === 'day_of_week' && typeof value === 'number') {
		const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
		// Convert 0-based day number to day name
		const dayIndex = value % 7;
		if (dayIndex >= 0 && dayIndex < 7) {
			return dayNames[dayIndex];
		}
		return String(value); // Fallback if invalid day number
	}

	// Handle number to quarter conversion
	if (convertNumberTo === 'quarter' && typeof value === 'number') {
		if (value >= 1 && value <= 4) {
			return `Q${value}`;
		}
		return String(value); // Fallback if invalid quarter number
	}

	if (columnType === 'date') {
		const date = dayjs(value);

		// Check if date is valid
		if (!date.isValid()) {
			return String(value);
		}

		if (!dateFormat) {
			return date.format('MMM D');
		}

		if (dateFormat === 'auto') {
			const AUTO_DATE_FORMATS = {
				datetime: 'MMM D, YYYY, h:mm:ss a', // e.g., "Mar 19, 2024, 2:30:45 pm"
				millisecond: 'h:mm:ss.SSS a', // e.g., "2:30:45.123 pm"
				second: 'h:mm:ss a', // e.g., "2:30:45 pm"
				minute: 'h:mm a', // e.g., "2:30 pm"
				hour: 'MMM D, hA', // e.g., "Mar 19, 2PM"
				day: 'MMM D', // e.g., "Mar 19"
				week: 'll', // e.g., "Mar 19, 2024"
				month: 'MMM YYYY', // e.g., "Mar 2024"
				quarter: '[Q]Q - YYYY', // e.g., "Q1 - 2024"
				year: 'YYYY', // e.g., "2024"
			};
			switch (xAxisTimeInterval) {
				case 'day':
					return date.format(AUTO_DATE_FORMATS.day);
				case 'week':
					// TODO: Implement week formatting
					return date.format(AUTO_DATE_FORMATS.week);
				case 'month':
					return date.format(AUTO_DATE_FORMATS.month);
				case 'quarter':
					return date.format(AUTO_DATE_FORMATS.quarter);
				case 'year':
					return date.format(AUTO_DATE_FORMATS.year);
				default:
					return date.format(AUTO_DATE_FORMATS.day);
			}
		}

		// Format based on dateFormat configuration - dayjs can format directly
		return date.format(dateFormat);
	}

	if (displayNumberAs === 'currency' && typeof value === 'number') {
		const adjustedValue = value * multiplier;

		// Handle compact numbers (1k, 1.2k, etc.)
		if (compactNumbers) {
			return formatCompactNumber(adjustedValue, {
				style: 'currency',
				currency: currency || 'USD',
				minimumFractionDigits,
				maximumFractionDigits,
			});
		}

		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: currency || 'USD',
			minimumFractionDigits,
			maximumFractionDigits,
		}).format(adjustedValue);
	}

	if (displayNumberAs === 'percent' && typeof value === 'number') {
		return new Intl.NumberFormat('en-US', {
			style: 'percent',
			minimumFractionDigits,
			maximumFractionDigits,
		}).format(value);
	}

	if (typeof value === 'number') {
		const adjustedValue = value * multiplier;

		// Handle compact numbers (1k, 1.2k, etc.)
		if (compactNumbers) {
			return formatCompactNumber(adjustedValue, {
				minimumFractionDigits,
				maximumFractionDigits,
			});
		}

		// Handle number separator style
		const formatOptions: Intl.NumberFormatOptions = {
			minimumFractionDigits,
			maximumFractionDigits,
		};

		// Apply number separator style
		// if (numberSeparatorStyle === ',') {
		// 	formatOptions.useGrouping = true;
		// } else if (
		// 	numberSeparatorStyle === null ||
		// 	numberSeparatorStyle === 'none'
		// ) {
		// 	formatOptions.useGrouping = false;
		// }

		const formattedNumber = adjustedValue.toLocaleString(
			'en-US',
			formatOptions
		);
		return `${safePrefix}${formattedNumber}${safeSuffix}`;
	}

	return String(value);
}

/**
 * Converts a string to human-readable title case
 * @param str - The string to convert
 * @returns The string in title case format
 */
export function toTitleCase(str: string): string {
	if (!str) return '';

	return str
		.replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before capital letters in camelCase
		.replace(/[_-]+/g, ' ') // Replace underscores and hyphens with spaces
		.replace(/\s+/g, ' ') // Replace multiple spaces with single space
		.trim() // Remove leading/trailing whitespace
		.split(' ')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(' ');
}

const widths = [
	0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
	0, 0, 0, 0, 0, 0, 0.2796875, 0.2765625, 0.3546875, 0.5546875, 0.5546875,
	0.8890625, 0.665625, 0.190625, 0.3328125, 0.3328125, 0.3890625, 0.5828125,
	0.2765625, 0.3328125, 0.2765625, 0.3015625, 0.5546875, 0.5546875, 0.5546875,
	0.5546875, 0.5546875, 0.5546875, 0.5546875, 0.5546875, 0.5546875, 0.5546875,
	0.2765625, 0.2765625, 0.584375, 0.5828125, 0.584375, 0.5546875, 1.0140625,
	0.665625, 0.665625, 0.721875, 0.721875, 0.665625, 0.609375, 0.7765625,
	0.721875, 0.2765625, 0.5, 0.665625, 0.5546875, 0.8328125, 0.721875, 0.7765625,
	0.665625, 0.7765625, 0.721875, 0.665625, 0.609375, 0.721875, 0.665625,
	0.94375, 0.665625, 0.665625, 0.609375, 0.2765625, 0.3546875, 0.2765625,
	0.4765625, 0.5546875, 0.3328125, 0.5546875, 0.5546875, 0.5, 0.5546875,
	0.5546875, 0.2765625, 0.5546875, 0.5546875, 0.221875, 0.240625, 0.5, 0.221875,
	0.8328125, 0.5546875, 0.5546875, 0.5546875, 0.5546875, 0.3328125, 0.5,
	0.2765625, 0.5546875, 0.5, 0.721875, 0.5, 0.5, 0.5, 0.3546875, 0.259375,
	0.353125, 0.5890625,
];

const avg = 0.5279276315789471;

function measureTextApproximate(str: string, fontSize: number) {
	return (
		Array.from(str).reduce(
			(acc, cur) => acc + (widths[cur.charCodeAt(0)] ?? avg),
			0
		) * fontSize
	);
}

export function getTextWidth(
	text: string,
	font: string,
	fontSize: number
): number {
	// re-use canvas object for better performance
	const canvas: HTMLCanvasElement =
		// @ts-expect-error ts(2339)
		getTextWidth.canvas ||
		// @ts-expect-error ts(2339)
		(getTextWidth.canvas = document.createElement('canvas'));

	const context = canvas.getContext('2d');
	if (!context) {
		return measureTextApproximate(text, fontSize);
	}
	context.font = `${fontSize}px ${font}`;
	const metrics = context.measureText(text);
	return metrics.width;
}

export function getTextHeight(
	text: string,
	font: string,
	fontSize: number
): number {
	const canvas: HTMLCanvasElement =
		// @ts-expect-error ts(2339)
		getTextHeight.canvas ||
		// @ts-expect-error ts(2339)
		(getTextHeight.canvas = document.createElement('canvas'));
	const context = canvas.getContext('2d');
	if (!context) {
		return fontSize * 1.2;
	}
	context.font = `${fontSize}px ${font}`;
	const metrics = context.measureText(text);
	return metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent;
}
